"use client";

import { Chess } from "chess.js";
import type { StockfishEngine } from "./engine";
import type { Score } from "./uci";
import { classifyMove, gameAccuracy, moveAccuracy, winPercent, type Classification } from "./winchance";
import type { PlayedMove } from "./game";

export type PositionEval = { score: Score; pv: string[]; best?: string };

export type MoveReview = {
  ply: number;
  san: string;
  uci: string;
  color: "w" | "b";
  fenBefore: string;
  before: PositionEval;
  after: PositionEval;
  moverWinBefore: number;
  moverWinAfter: number;
  accuracy: number;
  classification: Classification;
};

export type GameReview = {
  moves: MoveReview[];
  accuracy: { w: number; b: number };
  counts: { w: Partial<Record<Classification, number>>; b: Partial<Record<Classification, number>> };
  /** White's win percentage after each ply, starting with the initial position. */
  curve: number[];
};

/**
 * Analyses every position of a game and classifies each move. Positions that
 * are already decided (mate or stalemate) are scored without the engine.
 */
export async function reviewGame(
  engine: StockfishEngine,
  startFen: string,
  moves: PlayedMove[],
  opts: { depth?: number; onProgress?: (done: number, total: number) => void; signal?: AbortSignal; bookPlies?: number } = {},
): Promise<GameReview> {
  const depth = opts.depth ?? 12;
  const fens = [startFen, ...moves.map((m) => m.fen)];
  const evals: PositionEval[] = [];
  for (let i = 0; i < fens.length; i++) {
    if (opts.signal?.aborted) throw new DOMException("aborted", "AbortError");
    const c = new Chess(fens[i]);
    if (c.isCheckmate()) evals.push({ score: { mate: 0 }, pv: [] });
    else if (c.isDraw() || c.isStalemate()) evals.push({ score: { cp: 0 }, pv: [] });
    else {
      const r = await engine.analyse(fens[i], { depth, signal: opts.signal });
      const line = r.lines[0];
      evals.push({ score: line?.score ?? { cp: 0 }, pv: line?.pv ?? [], best: r.bestmove ?? undefined });
    }
    opts.onProgress?.(i + 1, fens.length);
  }

  // A "mate 0" score means the side to move is mated: express it from White's view.
  const whiteView = (e: PositionEval, fen: string): Score => {
    if (e.score.mate === 0) return { mate: fen.split(" ")[1] === "w" ? -1 : 1 };
    return e.score;
  };

  const curve = evals.map((e, i) => winPercent(whiteView(e, fens[i])));
  const out: MoveReview[] = [];
  const accs: Record<"w" | "b", number[]> = { w: [], b: [] };
  const counts: GameReview["counts"] = { w: {}, b: {} };
  moves.forEach((m, i) => {
    const color = fens[i].split(" ")[1] as "w" | "b";
    const wb = curve[i];
    const wa = curve[i + 1];
    const moverWinBefore = color === "w" ? wb : 100 - wb;
    const moverWinAfter = color === "w" ? wa : 100 - wa;
    const legal = new Chess(fens[i]).moves().length;
    const classification = classifyMove({
      moverWinBefore,
      moverWinAfter,
      playedUci: m.uci,
      bestUci: evals[i].best ?? evals[i].pv[0],
      legalMoveCount: legal,
      isBook: i < (opts.bookPlies ?? 0),
    });
    const accuracy = moveAccuracy(moverWinBefore, moverWinAfter);
    accs[color].push(accuracy);
    counts[color][classification] = (counts[color][classification] ?? 0) + 1;
    out.push({
      ply: i + 1,
      san: m.san,
      uci: m.uci,
      color,
      fenBefore: fens[i],
      before: { ...evals[i], score: whiteView(evals[i], fens[i]) },
      after: { ...evals[i + 1], score: whiteView(evals[i + 1], fens[i + 1]) },
      moverWinBefore,
      moverWinAfter,
      accuracy,
      classification,
    });
  });
  return { moves: out, accuracy: { w: gameAccuracy(accs.w), b: gameAccuracy(accs.b) }, counts, curve };
}
